import {
  SystemEntity,
  ReferenceType,
  ReferenceSource,
  ReferenceEntityType,
} from '@erp/shared-types';
import { useMemo } from 'react';
import { T, Var, useGT } from 'gt-react';
import { cn } from '@/lib/utils';
import { LinkIcon } from '@phosphor-icons/react';
import { Badge } from '@/components/ui/badge';
import { SystemEntityInline } from '@/components/system-entities/inline-ui';
import { useExternalSystemReference } from '@/api/rpc/hooks/core/useExternalSystemReferences';

interface ExternalSystemReferencePreviewProps {
  entityType: SystemEntity;
  entityId: string;
  className?: string;
}

const SOURCE_VARIANTS: Record<
  ReferenceSource,
  'default' | 'secondary' | 'outline'
> = {
  [ReferenceSource.DocumentExtraction]: 'secondary',
  [ReferenceSource.EdiInbound]: 'default',
  [ReferenceSource.EdiOutbound]: 'default',
  [ReferenceSource.ErpSync]: 'outline',
  [ReferenceSource.ManualEntry]: 'outline',
  [ReferenceSource.Import]: 'outline',
};

/**
 * Maps ReferenceEntityType to the corresponding SystemEntity for inline rendering.
 */
const ENTITY_TYPE_TO_SYSTEM_ENTITY: Record<ReferenceEntityType, SystemEntity> =
  {
    [ReferenceEntityType.Invoice]: SystemEntity.AP_INVOICE,
    [ReferenceEntityType.SalesOrder]: SystemEntity.AR_SALES_ORDER,
    [ReferenceEntityType.TransferOrder]: SystemEntity.IM_TRANSFER_ORDER,
    [ReferenceEntityType.PurchaseOrder]: SystemEntity.INTERNAL_PURCHASE_ORDER,
    [ReferenceEntityType.BillOfLading]: SystemEntity.SCM_SHIPMENT,
    [ReferenceEntityType.Shipment]: SystemEntity.SCM_SHIPMENT,
    [ReferenceEntityType.ProofOfDelivery]: SystemEntity.SCM_SHIPMENT,
  };

export function ExternalSystemReferencePreview({
  entityId,
  className,
}: ExternalSystemReferencePreviewProps) {
  const gt = useGT();
  const { data, isLoading, error } = useExternalSystemReference(entityId);

  const referenceTypeLabels = useMemo(
    (): Record<ReferenceType, string> => ({
      [ReferenceType.PurchaseOrder]: gt('Purchase Order', {
        context: 'external reference type',
      }),
      [ReferenceType.SalesOrder]: gt('Sales Order', {
        context: 'external reference type',
      }),
      [ReferenceType.TransferOrder]: gt('Transfer Order', {
        context: 'external reference type',
      }),
      [ReferenceType.Invoice]: gt('Invoice', {
        context: 'external reference type',
      }),
      [ReferenceType.BillOfLading]: gt('Bill of Lading', {
        context: 'external reference type',
      }),
      [ReferenceType.Shipment]: gt('Shipment', {
        context: 'external reference type',
      }),
      [ReferenceType.ProofOfDelivery]: gt('Proof of Delivery', {
        context: 'external reference type',
      }),
      [ReferenceType.Other]: gt('Other', {
        context: 'external reference type',
      }),
    }),
    [gt],
  );

  const sourceLabels = useMemo(
    (): Record<ReferenceSource, string> => ({
      [ReferenceSource.DocumentExtraction]: gt('Document Extraction', {
        context: 'reference source',
      }),
      [ReferenceSource.EdiInbound]: gt('EDI Inbound', {
        context: 'reference source',
      }),
      [ReferenceSource.EdiOutbound]: gt('EDI Outbound', {
        context: 'reference source',
      }),
      [ReferenceSource.ErpSync]: gt('ERP Sync', {
        context: 'reference source',
      }),
      [ReferenceSource.ManualEntry]: gt('Manual Entry', {
        context: 'reference source',
      }),
      [ReferenceSource.Import]: gt('Import', {
        context: 'reference source',
      }),
    }),
    [gt],
  );

  if (isLoading) {
    return (
      <div className={cn('min-w-64 max-w-sm p-3', className)}>
        <div className="flex items-center gap-2 mb-2">
          <LinkIcon className="size-4 text-muted-foreground" />
          <h4 className="text-sm font-semibold">
            <T>External Reference</T>
          </h4>
        </div>
        <p className="text-xs text-muted-foreground">
          <T>Loading...</T>
        </p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className={cn('min-w-64 max-w-sm p-3', className)}>
        <div className="flex items-center gap-2 mb-2">
          <LinkIcon className="size-4 text-muted-foreground" />
          <h4 className="text-sm font-semibold">
            <T>External Reference</T>
          </h4>
        </div>
        <p className="text-xs text-muted-foreground">
          <T>Unable to load reference details</T>
        </p>
      </div>
    );
  }

  const typeLabel =
    referenceTypeLabels[data.referenceType] ??
    gt('Reference', {
      context: 'external reference type fallback',
    });

  return (
    <div className={cn('min-w-64 max-w-sm p-3', className)}>
      <div className="flex items-center gap-2 mb-3">
        <LinkIcon className="size-4 text-muted-foreground" />
        <h4 className="text-sm font-semibold">
          <T>
            <Var>{typeLabel}</Var> Reference
          </T>
        </h4>
      </div>

      <div className="space-y-2 text-xs">
        <div>
          <span className="text-muted-foreground">
            {gt('Value:', { context: 'external reference field label' })}{' '}
          </span>
          <span className="font-medium font-mono">{data.referenceValue}</span>
        </div>

        <div className="flex items-center gap-1.5">
          <span className="text-muted-foreground">
            {gt('Source:', { context: 'external reference field label' })}{' '}
          </span>
          <Badge
            variant={SOURCE_VARIANTS[data.source] ?? 'outline'}
            className="text-xs"
          >
            {sourceLabels[data.source] ?? data.source}
          </Badge>
        </div>

        {data.sourceSystem && (
          <div>
            <span className="text-muted-foreground">
              {gt('System:', {
                context: 'external reference field label',
              })}{' '}
            </span>
            <span className="font-medium capitalize">{data.sourceSystem}</span>
          </div>
        )}

        {data.confidence != null && (
          <div>
            <span className="text-muted-foreground">
              {gt('Confidence:', {
                context: 'external reference field label',
              })}{' '}
            </span>
            <span className="font-medium">
              {Math.round(data.confidence * 100)}%
            </span>
          </div>
        )}

        {data.rawText && data.rawText !== data.referenceValue && (
          <div>
            <span className="text-muted-foreground">
              {gt('Raw Text:', {
                context: 'external reference field label',
              })}{' '}
            </span>
            <span className="font-medium text-muted-foreground italic">
              {data.rawText}
            </span>
          </div>
        )}

        {data.entityType && data.entityId && (
          <div>
            <span className="text-muted-foreground">
              {gt('Linked Entity:', {
                context: 'external reference field label',
              })}{' '}
            </span>
            <SystemEntityInline
              entityType={ENTITY_TYPE_TO_SYSTEM_ENTITY[data.entityType]}
              entityId={data.entityId}
              className="text-xs"
            />
          </div>
        )}
      </div>
    </div>
  );
}
